import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  ViewChild,
  InjectionToken,
  Inject
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { filtermodels, SortFilter, filtermodel } from '../../../../../model/sort-filter';
import { ApplicationAuditLog } from '../../../../../model/application-audit-log';
// See C001 - 2026-04-17 - HE
// import { LoggingService } from '../../../../../services.module';
import { LoggingService } from '../../../../../services/logging.service';
import { Environment } from '../../../../../model/environment';

import { tap, merge } from 'rxjs';
import { ApplicationLogTableDataSource } from './applicationlog-table-datasource';
import { Router, ActivatedRoute } from '@angular/router';
import { ApplicationLogComponent } from '../applicationlog/applicationlog.component';
import { FilterDialogComponent, FilterData } from './filter-dialog.component';
import { ActiveFiltersComponent } from '../../../../shared/active-filters/active-filters.component';

const ConfigService = new InjectionToken<Environment>('EnvironmentConfig');

@Component({
  selector: 'app-applicationlog-table',
  templateUrl: './applicationlog-table.component.html',
  styleUrls: ['./applicationlog-table.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    ActiveFiltersComponent
  ],
  providers: [
    {
      provide: ConfigService,
      useValue: {
        production: false,
        companyName: '',
        companyWWWURL: '',
        appVersion: '',
        appAPIVersion: '',
        useServicepointAccess: false,
        logo: '',
        fullLogo: '',
        logoLink: '',
        logoTitle: '',
        logoStyle: '',
        logoPadding: '',
        defaultSidebarColor: '',
        loginBackground: '',
        currencyCode: '',
        currency: '',
        REST_SERVER_HOST: '',
        REST_SERVER_url: [],
        debug: false,
        auth: {
          clientID: '',
          domain: '',
          responseType: '',
          audience: '',
          redirect: '',
          scope: ''
        },
        logoutURL: '',
        MINUTES_UNITL_AUTO_LOGOUT: 0,
        CHECK_INTERVALL: 0
      }
    }
  ]
})
export class ApplicationLogTableComponent implements AfterViewInit, OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  dataSource!: ApplicationLogTableDataSource;
  selection = new SelectionModel<ApplicationAuditLog>(false, []);

  // private search$:  BehaviorSubject<searchIF[] | null> = new BehaviorSubject(null);
  private search$: EventEmitter<filtermodels> = new EventEmitter();

  public searchInterface: filtermodels = {};

  _sortFilter!: SortFilter;
  sortFilter!: any;
  @Input() applicationId = ''; // Input property for applicationId/clientID
  @Input() applicationObj: any = { clientId: '' }; // Keep for backward compatibility

  // Default date for logDate filter - TK 08/06/2025
  defaultLogDate = '';

  // Default filters object to track default values - TK 01/15/2025
  defaultFilters: filtermodels = {};

  /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
  displayedColumns = [
    'logDate',
    'logID',
    'typeDescription',
    'description',
    'applicationName',
    'applicationId',
    'ip',
    'userName',
    'recordDate',
    'recordTime',
  ];

  headerMap = new Map()
    .set('logDate', { label: 'Log Date', type: 'date', filter: true })
    .set('logID', { label: 'Log ID', type: 'text', filter: true })
    .set('typeDescription', { label: 'Type Description', type: 'text', filter: true })
    .set('description', { label: 'Description', type: 'text', filter: true })
    .set('applicationName', { label: 'Application Name', type: 'text', filter: true })
    .set('applicationId', { label: 'Application ID', type: 'text', filter: true })
    .set('ip', { label: 'IP', type: 'text', filter: true })
    .set('userName', { label: 'User Name', type: 'text', filter: true })
    .set('recordDate', { label: 'Record Date', type: 'date', filter: true })
    .set('recordTime', { label: 'Record Time', type: 'text', filter: true });

  constructor(
    private _loggingService: LoggingService,
    private dialog: MatDialog,
    private elRef: ElementRef,
    private _router: Router,
    private _route: ActivatedRoute,
    @Inject(ConfigService) private environment: Environment
  ) {}

  ngOnInit() {
    this.dataSource = new ApplicationLogTableDataSource(this._loggingService);

    // Create default filter for today's date minus 2 days - TK 01/15/2025
    const today = new Date();
    const twoDaysAgo = new Date(today);
    twoDaysAgo.setDate(today.getDate() - 2);

    const formattedDate = this.formatDate(twoDaysAgo);

    console.log('Date calculation - Today:', this.formatDate(today));
    console.log('Date calculation - Two days ago:', formattedDate);

    // Set default date for the input field - TK 01/15/2025
    this.defaultLogDate = formattedDate;

    // Set up default filters object - TK 01/15/2025
    this.defaultFilters = {
      logDate: {
        filter: formattedDate,
        filterType: 'date',
        type: 'greaterThanEqual',
        dateFrom: formattedDate
      }
    };

    // Don't initialize sortFilter here - let the subscription handle it - TK 01/15/2025

    // Get applicationId from query parameters or input properties - TK 01/15/2025
    this._route.queryParams.subscribe(params => {
      if (params['applicationId']) {
        this.applicationId = params['applicationId'];
      }
    });

    // Don't load data here - let ngAfterViewInit handle it with proper filters - TK 01/15/2025
  }

  ngAfterViewInit() {
    this.paginator.pageSize = 10;
    const el: HTMLElement = this.elRef.nativeElement;
    const all = el.getElementsByClassName('table-input');

    // Set up default date filter after view is initialized - TK 01/15/2025
    setTimeout(() => {
      console.log('Setting up default filter, defaultLogDate:', this.defaultLogDate);

      if (this.defaultLogDate) {
        // Set the filter directly in searchInterface since no input fields exist - TK 01/15/2025
        const filter: filtermodel = {
          filter: this.defaultLogDate, // Use the date as the filter value - TK 01/15/2025
          filterType: 'date',
          type: 'greaterThanEqual',
          dateFrom: this.defaultLogDate
        };
        this.searchInterface['logDate'] = filter;
        console.log('Setting default filter:', this.searchInterface);
        this.search$.emit(this.searchInterface);
      } else {
        console.log('No default date, loading without filters');
        // Load data without any filters if no default filter can be set - TK 01/15/2025
        this.search$.emit({});
      }
    }, 100);

    // No input fields in template, so skip input field handling - TK 01/15/2025
    // Input filtering is handled through the global filter dialog instead

    const sm: any[] = [];
    this.search$.subscribe((fm) => {
      console.log('Filter applied:', fm);

      // Create clean sort filter without empty structure when no filters - TK 01/15/2025
      if (!fm || Object.keys(fm).length === 0) {
        this.sortFilter = {
          sort: [],
          filter: undefined
        };
      } else {
        this._sortFilter = new SortFilter(this.environment);
        this.sortFilter = this._sortFilter.createSortFilter(sm, fm);

        // Remove empty first filter if it exists - TK 01/15/2025
        if (this.sortFilter.filter?.Filters && this.sortFilter.filter.Filters.length > 0) {
          const firstFilter = this.sortFilter.filter.Filters[0];
          if (firstFilter.column1 === '' && firstFilter.columnfilter1?.filter === '') {
            this.sortFilter.filter.Filters.shift();
          }
        }
      }

      const clientId = this.applicationId || this.applicationObj.clientId;
      this.dataSource.loadApplicationLogs(
        clientId,
        this.paginator.pageIndex,
        this.paginator.pageSize,
        this.sort.active,
        this.sort.direction,
        this.sortFilter
      );
    });

    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    this.dataSource.counter$
      .pipe(
        tap((count) => {
          this.paginator.length = count;
        })
      )
      .subscribe();

    merge(this.paginator.page, this.sort.sortChange)
      .pipe(
        tap(() => {
          const clientId = this.applicationId || this.applicationObj.clientId;
          this.dataSource.loadApplicationLogs(
            clientId,
            this.paginator.pageIndex,
            this.paginator.pageSize,
            this.sort.active,
            this.sort.direction,
            this.sortFilter
          );
        })
      )
      .subscribe();
  }

  showDetail(row: any, action: string): void {
    console.log(row);
    const dialogConfig = new MatDialogConfig();

    dialogConfig.autoFocus = true;
    dialogConfig.minWidth = '80vw';
    dialogConfig.minHeight = '50vh';
    dialogConfig.maxHeight = '90vh';
    dialogConfig.data = { applicationLog: row, action: action };

    const dialogRef = this.dialog.open(
      ApplicationLogComponent,
      dialogConfig
    );

    dialogRef.afterClosed().subscribe((result) => {
      console.log('The dialog was closed');
      console.log(result);
      const clientId = this.applicationId || this.applicationObj.clientId;
      this.dataSource.loadApplicationLogs(
        clientId,
        this.paginator.pageIndex,
        this.paginator.pageSize,
        this.sort.active,
        this.sort.direction,
        this.sortFilter
      );
    });
  }

  setupFilter(column: string) {
    console.log(column);
  }

  openFilterDialog(column: string) {
    console.log('Opening filter dialog for:', column);
    // TODO: Implement filter dialog - TK 01/15/2025
    // This will open a dialog with filter options for the specific column
  }

  openGlobalFilterDialog() {
    console.log('Opening global filter dialog');

    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '600px';
    dialogConfig.maxHeight = '80vh';
    dialogConfig.data = {
      columns: this.displayedColumns,
      headerMap: this.headerMap,
      defaultLogDate: this.defaultLogDate,
      currentFilters: this.searchInterface // Pass current filters to dialog - TK 01/15/2025
    } as FilterData;

    const dialogRef = this.dialog.open(FilterDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((result: { [key: string]: any } | undefined) => {
      if (result && Object.keys(result).length > 0) {
        console.log('Applied filters:', result);
        this.applyFilters(result);
      } else if (result && Object.keys(result).length === 0) {
        console.log('Clearing all filters');
        this.clearFilters();
      }
    });
  }

    applyFilters(filters: { [key: string]: any }) {
    console.log('Applying filters:', filters);

    // Clear existing searchInterface to start fresh - TK 01/15/2025
    this.searchInterface = {};

    // Convert dialog filters to the format expected by the service - TK 01/15/2025
    Object.keys(filters).forEach(column => {
      const filterValue = filters[column];

      if (typeof filterValue === 'string' && filterValue.trim() !== '') {
        // Text filter - follow same pattern as user table - TK 01/15/2025
        this.searchInterface[column] = {
          filter: filterValue,
          filterType: this.headerMap.get(column)?.type || 'text',
          type: 'contains'
        };
      } else if (typeof filterValue === 'object' && filterValue !== null) {
        // Date range filter - follow same pattern as user table - TK 01/15/2025
        const dateFilter = filterValue as { from: Date | null; to: Date | null };

        if (dateFilter.from || dateFilter.to) {
                              if (dateFilter.from && dateFilter.to) {
            // Both dates provided - use dateRange (even if same date) - TK 01/15/2025
            const fromDate = this.formatDate(dateFilter.from);
            const toDate = this.formatDate(dateFilter.to);

            this.searchInterface[column] = {
              filter: fromDate,
              type: 'inRange',
              filterType: 'date',
              dateFrom: fromDate,
              dateTo: toDate
            };
          } else if (dateFilter.from) {
            // Only from date - use equals pattern like user table - TK 01/15/2025
            const fromDate = this.formatDate(dateFilter.from);
            this.searchInterface[column] = {
              filter: fromDate,
              type: 'greaterThanEqual',
              filterType: 'date',
              dateFrom: fromDate
            };
          } else if (dateFilter.to) {
            // Only to date - use equals pattern like user table - TK 01/15/2025
            const toDate = this.formatDate(dateFilter.to);
            this.searchInterface[column] = {
              filter: toDate,
              type: 'equals',
              filterType: 'date',
              dateFrom: toDate
            };
          }
        }
      }
    });

    // Emit the searchInterface to trigger data loading - follow user table pattern - TK 01/15/2025
    console.log('Updated searchInterface:', this.searchInterface);
    this.search$.emit(this.searchInterface);
  }

  clearFilters() {
    console.log('Clearing all filters');

    // Reset to default filters instead of clearing completely - TK 01/15/2025
    this.searchInterface = { ...this.defaultFilters };
    this.search$.emit(this.searchInterface);
  }

  clearFilter(column: string) {
    console.log('Clearing filter for column:', column);

    // Check if this is a default filter - TK 01/15/2025
    const defaultFilter = this.defaultFilters[column];
    if (defaultFilter && JSON.stringify(this.searchInterface[column]) === JSON.stringify(defaultFilter)) {
      console.log('Cannot clear default filter:', column);
      return; // Don't clear default filters
    }

    // Remove specific filter from searchInterface - TK 01/15/2025
    if (this.searchInterface[column]) {
      delete this.searchInterface[column];

      // Check if this was the last non-default filter - TK 01/15/2025
      const remainingFilters = Object.keys(this.searchInterface).length;
      const hasDefaultFilters = Object.keys(this.defaultFilters).length > 0;

      // If no filters remain and we have default filters, reset to defaults - TK 01/15/2025
      if (remainingFilters === 0 && hasDefaultFilters) {
        console.log('Last filter cleared, resetting to default filters');
        this.searchInterface = { ...this.defaultFilters };
      }

      this.search$.emit(this.searchInterface);
    }
  }

  selectedRow: any;
  highlight(row: any) {
    this.selectedRow = row;
  }

  // Navigate back to Applications table 21/07/2026 DW
  goBack() {
    this._router.navigate(['/auth0/application']).then();
  }

  // Helper method to format dates in timezone-safe way - TK 01/15/2025
  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

}

// ========================================
// CHANGE LOG
// ========================================
// C001: Changed LoggingService import from '../../../../../services.module' to '../../../../../services/logging.service'. Previous behavior: LoggingService is registered in ServicesModule providers but is not exported from services.module.ts as a named export, causing TS2459 and TS-992003 (no injection token) when ApplicationLogTableComponent constructor used the type imported from the module barrel path. New behavior: Import LoggingService from logging.service.ts where @Injectable is declared. Reason: Fix portaladmin build. Context: Standalone ApplicationLogTableComponent. Impact: DI unchanged (LoggingService providedIn: 'root'). - 2026-04-17 - HE
